import { formatDuration } from '../utils/time'

export type ProgressInfo = {
  fetched: number
  pages: number
  rate: number
  elapsedMs: number
  estimatedRemainingMs: number | null
  guessTotal: number
  hitCache: boolean
  done: boolean
}

export function ProgressOverlay({
  info,
  onCancel,
}: {
  info: ProgressInfo | null
  onCancel: () => void
}) {
  if (!info) return null
  const pct = Math.min(
    99,
    Math.round((info.fetched / Math.max(info.guessTotal, info.fetched + 1)) * 100),
  )
  const slow = info.estimatedRemainingMs != null && info.estimatedRemainingMs > 60_000
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/50 backdrop-blur-sm">
      <div className="glass-strong w-full max-w-md p-6">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-base font-bold">Loading entire feed</h3>
          {!info.done ? (
            <button type="button" className="glass-button text-xs" onClick={onCancel}>
              cancel
            </button>
          ) : null}
        </div>
        <p className="mb-4 text-xs text-ink-300">
          {info.done
            ? 'Done.'
            : 'Walking back through history. Records are cached so the next visit only fetches what is new.'}
        </p>
        {slow ? (
          <p className="mb-3 rounded-xl border border-amber-300/30 bg-amber-300/10 px-3 py-2 text-xs text-amber-100">
            Heads up — this looks like it will take more than a minute. Cancel any time;
            already-fetched records stay cached.
          </p>
        ) : null}
        <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-2 rounded-full transition-all"
            style={{ width: `${pct}%`, background: 'var(--accent-strong)' }}
          />
        </div>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-ink-300">Fetched</dt>
          <dd className="text-right font-mono">{info.fetched.toLocaleString()}</dd>
          <dt className="text-ink-300">Pages</dt>
          <dd className="text-right font-mono">{info.pages}</dd>
          <dt className="text-ink-300">Rate</dt>
          <dd className="text-right font-mono">{info.rate.toFixed(1)} rec/s</dd>
          <dt className="text-ink-300">Elapsed</dt>
          <dd className="text-right font-mono">{formatDuration(info.elapsedMs)}</dd>
          {info.estimatedRemainingMs != null ? (
            <>
              <dt className="text-ink-300">Est. remaining</dt>
              <dd className="text-right font-mono">{formatDuration(info.estimatedRemainingMs)}</dd>
            </>
          ) : null}
        </dl>
      </div>
    </div>
  )
}
